import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '../assets/css/homepage.css';
import { productsAPI, categoriesAPI, wishlistAPI, dealsAPI, recommendationsAPI, promotionsAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { useSwap } from '../context/SwapContext';
import { useAPI } from '../hooks/useAPI';
import Loader from './Loader';
import AnimatedAddToCartButton from './AnimatedAddToCartButton';
import AnimatedViewButton from './AnimatedViewButton';
import SwapOfferForm from './SwapOfferForm';
import '../assets/css/modern-product-card.css';

gsap.registerPlugin(ScrollTrigger);

// Product Image with Hover Effect Component
const ProductImageWithHover = ({ product }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const intervalRef = useRef(null);

  // Extract images from product
  const getProductImages = () => {
    const proxyImageUrl = (url) => {
      if (!url) return url;
      // If already proxied, return as is
      if (url.startsWith('/api/v1/proxy/image?url=') || url.includes('/api/v1/proxy/image?url=')) {
        return url;
      }
      // Only proxy pointekonline which has CORS issues; CDN uploads (ImgBB, Imgur, etc.) work directly
      if (url.startsWith('http') && url.includes('pointekonline.com')) {
        return `/api/v1/proxy/image?url=${encodeURIComponent(url)}`;
      }
      return url;
    };

    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      // Backend format: array of objects with url property
      return product.images.map(img => {
        const url = typeof img === 'string' ? img : img.url;
        return proxyImageUrl(url);
      });
    } else if (product.image) {
      // Fallback to single image
      return [proxyImageUrl(product.image)];
    } else {
      // Default placeholder
      return [null];
    }
  };

  const images = getProductImages();

  useEffect(() => {
    if (isHovering && images.length > 1) {
      // Start cycling through images every 2 seconds
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 2000);
    } else {
      // Reset to first image when not hovering
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setCurrentImageIndex(0);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovering, images.length]);

  return (
    <div
      className="AutoboyExpress-product-image-wrapper"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {images.filter(img => img).map((img, index) => (
        <img
          key={index}
          src={img}
          alt={`${product.name || product.title} - Image ${index + 1}`}
          className={`AutoboyExpress-deal-image ${index === currentImageIndex ? 'active' : ''}`}
          style={{
            opacity: index === currentImageIndex ? 1 : 0,
            transition: 'opacity 0.5s ease-in-out',
            position: index === 0 ? 'relative' : 'absolute',
            top: index === 0 ? 'auto' : 0,
            left: index === 0 ? 'auto' : 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      ))}
    </div>
  );
};

// 3D Tilt Card wrapper for deals
const TiltCard = ({ children, className, onClick }) => {
  const ref = useRef(null);
  const [rot, setRot] = useState({ x: 0, y: 0 });

  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    setRot({ x: (py - 0.5) * -10, y: (px - 0.5) * 10 });
  };

  const onLeave = () => {
    setRot({ x: 0, y: 0 });
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ transformStyle: 'preserve-3d', perspective: 800 }}
      animate={{ rotateX: rot.x, rotateY: rot.y }}
      transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.5 }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );
};

// ── Promo Stack Card (memoised) ──────────────────────────────────────────────
const PromoCard = memo(({ promo, index, activeIndex, total, onSwipeDone }) => {
  const offset = index - activeIndex;
  const absOffset = Math.abs(offset);
  const isActive = index === activeIndex;

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    e.currentTarget.style.setProperty('--mx', (e.clientX - rect.left - cx) / cx);
    e.currentTarget.style.setProperty('--my', (e.clientY - rect.top - cy) / cy);
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.setProperty('--mx', 0);
    e.currentTarget.style.setProperty('--my', 0);
  }, []);

  return (
    <motion.div
      className={`hp-promo-card${isActive ? ' hp-promo-card--active' : ''}`}
      style={{ '--mx': 0, '--my': 0, zIndex: total - absOffset, pointerEvents: isActive ? 'auto' : 'none' }}
      onMouseMove={isActive ? handleMouseMove : undefined}
      onMouseLeave={isActive ? handleMouseLeave : undefined}
      animate={{
        scale: 1 - absOffset * 0.04,
        y: absOffset * -14,
        opacity: absOffset > 3 ? 0 : 1 - absOffset * 0.15,
      }}
      transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      drag={isActive ? 'x' : false}
      dragConstraints={{ left: -120, right: 120 }}
      dragElastic={0.2}
      onDragEnd={(_, info) => {
        if (Math.abs(info.offset.x) > 80) onSwipeDone(info.offset.x < 0 ? 'left' : 'right');
      }}
    >
      <div className="hp-promo-card-inner">
        {/* Liquid gradient blobs — GPU-accelerated via transform3d */}
        <div className="hp-liquid-bg" aria-hidden="true">
          <div className="hp-liquid-blob hp-liquid-blob--1" />
          <div className="hp-liquid-blob hp-liquid-blob--2" />
          <div className="hp-liquid-blob hp-liquid-blob--3" />
        </div>
        {/* Optional real image blended on top of liquid */}
        {promo.image && (
          <div className="hp-promo-img-layer"
            style={{ backgroundImage: `url(${promo.image})` }} />
        )}
        <div className="hp-promo-overlay" />
        <div className="hp-promo-content">
          {promo.discount_percent && (
            <span className="hp-promo-pct-badge">{promo.discount_percent}% OFF</span>
          )}
          <h3 className="hp-promo-title">{promo.title || promo.name}</h3>
          {(promo.description || promo.subtitle) && (
            <p className="hp-promo-desc">{promo.description || promo.subtitle}</p>
          )}
          <a href={promo.cta_link || '/shop'} className="hp-promo-cta-btn" onClick={(e) => e.stopPropagation()}>
            {promo.cta_text || 'Shop Now'} <i className="fas fa-arrow-right" />
          </a>
        </div>
      </div>
    </motion.div>
  );
});

const Homepage = () => {
  const { addToCart } = useCart();
  const { showCustomToast } = useNotifications();
  const { user, isAuthenticated } = useAuth();
  const { submitSwapOffer } = useSwap();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [wishlistItems, setWishlistItems] = useState([]);
  // flashDeals: sitewide promo campaigns (Admin -> Marketing -> Sales
  // Campaigns) — a banner, not a product. Used only for the hero banner below.
  const [flashDeals, setFlashDeals] = useState([]);
  // flashDealProducts: real products the admin marked down (Admin -> Products
  // -> Flash Deal). This is what actually renders as product cards — a
  // campaign has no product behind it, so it can never correctly do that.
  const [flashDealProductsData, setFlashDealProductsData] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [flashNow, setFlashNow] = useState(Date.now());

  // ── New section state ────────────────────────────────────────────────────
  const [promos, setPromos] = useState([]);
  const [promoIndex, setPromoIndex] = useState(0);

  // ── Swap Zone state ──────────────────────────────────────────────────────
  const [swapZoneProducts, setSwapZoneProducts] = useState([]);
  const [swapZoneLoading, setSwapZoneLoading] = useState(false);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [selectedProductForSwap, setSelectedProductForSwap] = useState(null);

  const orb1Ref = useRef(null);
  const orb2Ref = useRef(null);
  const orb3Ref = useRef(null);

  // Check if user is a seller
  const isSeller = user?.user_type === 'seller' || user?.userType === 'seller';
  const isReseller = user?.is_reseller || user?.isReseller;


  const proxyImageUrl = (url) => {
    if (!url) return url;
    if (url.startsWith('/api/v1/proxy/image?url=') || url.includes('/api/v1/proxy/image?url=')) return url;
    // Only proxy pointekonline which has CORS issues; CDN uploads (ImgBB, Imgur, etc.) work directly
    if (url.startsWith('http') && url.includes('pointekonline.com')) {
      return `/api/v1/proxy/image?url=${encodeURIComponent(url)}`;
    }
    return url;
  };


  // Fetch data from Go backend
  // Pass include_seller_price parameter if user is a seller to get both buyer and seller prices
  const { data: productsData, loading: productsLoading } = useAPI(() =>
    productsAPI.getFeatured(isSeller ? { include_seller_price: true } : {})
  );
  const { data: categoriesData, loading: categoriesLoading } = useAPI(() => categoriesAPI.getAll());

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch products, categories, and wishlist in parallel
        const [productsData, categoriesData, wishlistResponse] = await Promise.all([
          productsAPI.getFeatured(isSeller ? { include_seller_price: true } : {}),
          categoriesAPI.getAll(),
          wishlistAPI.getWishlist().catch(() => ({ data: [] })) // Fail gracefully
        ]);

        // Process wishlist
        if (wishlistResponse && wishlistResponse.data) {
          const wishlistData = wishlistResponse.data.items || wishlistResponse.data || [];
          setWishlistItems(wishlistData.map(item => item.product_id || item.product?._id || item.product?.id));
        }

        // Process products
        if (productsData) {
          const rawProducts = productsData.data?.products || productsData.data || productsData.products || productsData;
          const products = Array.isArray(rawProducts) ? rawProducts : [];
          const activeProducts = products.filter(p => p.is_active !== false && p.deleted !== true);

          const mappedProducts = activeProducts.map(product => {
            const mappedProduct = {
              ...product,
              stock: product.quantity !== undefined ? product.quantity : (product.stock !== undefined ? product.stock : 0),
              name: product.title || product.name || 'Product',
              image: product.images && product.images.length > 0
                ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url)
                : (product.image)
            };


            return mappedProduct;
          });

          setProducts(Array.isArray(mappedProducts) ? mappedProducts : []);
        }

        // Process categories
        if (categoriesData) {
          const categories = categoriesData.data?.categories || categoriesData.data || categoriesData.categories || categoriesData || [];
          setCategories(Array.isArray(categories) ? categories : []);
        }
      } catch (error) {
        setProducts([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isSeller]);

  // Fetch flash deals + trending on mount
  useEffect(() => {
    const fetchHomeExtras = async () => {
      const mapProduct = (p) => ({
        ...p,
        name: p.title || p.name || 'Product',
        image: p.images?.[0]?.url || (typeof p.images?.[0] === 'string' ? p.images[0] : null) || p.image,
        stock: p.quantity ?? p.stock ?? 0,
      });

      const [flashRes, flashProductsRes, trendingRes] = await Promise.allSettled([
        dealsAPI.getFlashDeals(),
        productsAPI.getFlashDeals(),
        recommendationsAPI.getTrending(8),
      ]);

      if (flashRes.status === 'fulfilled' && flashRes.value?.data) {
        const deals = Array.isArray(flashRes.value.data) ? flashRes.value.data : flashRes.value.data.deals || [];
        setFlashDeals(deals);
      }
      if (flashProductsRes.status === 'fulfilled' && flashProductsRes.value?.data) {
        const raw = Array.isArray(flashProductsRes.value.data) ? flashProductsRes.value.data : flashProductsRes.value.data.products || [];
        setFlashDealProductsData(raw.map(mapProduct));
      }
      if (trendingRes.status === 'fulfilled' && trendingRes.value?.data) {
        const raw = Array.isArray(trendingRes.value.data) ? trendingRes.value.data : trendingRes.value.data.products || [];
        setTrendingProducts(raw.map(mapProduct));
      }
    };
    fetchHomeExtras();
  }, []);

  // Fetch personalized recommendations when user is logged in
  useEffect(() => {
    if (!user) return;
    const fetchRecs = async () => {
      try {
        const r = await recommendationsAPI.getPersonalized(8);
        if (r?.data) {
          const raw = Array.isArray(r.data) ? r.data : r.data.products || [];
          setRecommendations(raw.map(p => ({
            ...p,
            name: p.title || p.name || 'Product',
            image: p.images?.[0]?.url || (typeof p.images?.[0] === 'string' ? p.images[0] : null) || p.image,
            stock: p.quantity ?? p.stock ?? 0,
          })));
        }
      } catch { }
    };
    fetchRecs();
  }, [user]);

  // ── Fetch premium sellers + active promos on mount ──────────────────────

  useEffect(() => {
    const fetchSpotlightData = async () => {
      const [bannersRes, flashRes] = await Promise.allSettled([
        // Hot Promotions aggregate what's published in Admin → Marketing:
        promotionsAPI.getBanners(),  // banners  → GET /banners
        promotionsAPI.getActive(),   // flash sales → GET /deals/flash-deals
      ]);

      // Hot Promotions reflect what's published in Admin → Marketing: banners
      // (image + CTA) AND flash sales (title + discount %), both rendered as
      // promo cards. Sample promos only appear if BOTH requests fail outright.
      const pickList = (res, keys) => {
        if (res.status !== 'fulfilled') return null;
        const d = res.value?.data ?? res.value;
        if (Array.isArray(d)) return d;
        for (const k of keys) if (Array.isArray(d?.[k])) return d[k];
        return [];
      };
      const banners = pickList(bannersRes, ['banners', 'promotions', 'data']);
      const flashRaw = pickList(flashRes, ['deals', 'flash_sales', 'data']);
      // Map a flash sale (ExclusiveDeal) to the promo-card shape.
      const flashPromos = (flashRaw || []).map(fs => ({
        id: fs.id || fs._id,
        title: fs.title,
        description: fs.description || fs.subtitle,
        discount_percent: fs.discount_percent ?? fs.discount_percentage,
        image: fs.banner_image || fs.image || (fs.products?.[0]?.images?.[0]?.url),
        cta_text: 'Shop the Sale',
        cta_link: '/shop',
      }));

      // If both requests fail, show no promotions. This previously fell back
      // to invented ones ("Flash Summer Sale — up to 40% off", a ₦20,000
      // trade-in bonus) that advertised discounts the store does not offer.
      // The carousel already hides itself when the list is empty.
      setPromos([...(banners || []), ...flashPromos]);
    };
    fetchSpotlightData();
  }, []);

  // ── Auto-slide promo cards ─────────────────────────────────────────────────
  useEffect(() => {
    if (promos.length === 0) return;
    const autoSlideTimer = setInterval(() => {
      setPromoIndex(i => (i + 1) % promos.length);
    }, 5000); // Change card every 5 seconds
    return () => clearInterval(autoSlideTimer);
  }, [promos.length]);

  // ── Sample Swap Zone Products (fallback/demo data) ─────────────────────────

  // ── Fetch Swap Zone products ─────────────────────────────────────────────
  useEffect(() => {
    const fetchSwapProducts = async () => {
      setSwapZoneLoading(true);
      try {
        // Fetch swap-available products from backend
        // The API endpoint should filter for swap_available: true
        const res = await productsAPI.getAll({
          limit: 12,
          swap_available: true // Filter for swap-available products
        }).catch(() => null);

        if (res?.data) {
          const raw = Array.isArray(res.data) ? res.data : (res.data?.products || []);

          const swappable = raw
            .filter(p => p.swap_available !== false)
            .slice(0, 8);

          const mapped = swappable.map(p => {
            let deviceType = p.device_type || p.category || 'Device';
            if (deviceType.toLowerCase().includes('iphone') || deviceType.toLowerCase().includes('ios')) {
              deviceType = 'ios';
            } else if (deviceType.toLowerCase().includes('galaxy') || deviceType.toLowerCase().includes('android')) {
              deviceType = 'android';
            } else if (deviceType.toLowerCase().includes('macbook') || deviceType.toLowerCase().includes('laptop') || deviceType.toLowerCase().includes('computer')) {
              deviceType = 'laptop';
            }
            return {
              ...p,
              id: p._id || p.id,
              name: p.title || p.name || 'Product',
              image: p.images?.[0]?.url || (typeof p.images?.[0] === 'string' ? p.images[0] : null) || p.image,
              stock: p.quantity ?? p.stock ?? 0,
              min_swap_value: p.min_swap_value || p.trade_in_value || Math.floor((p.price || 0) * 0.4),
              device_type: deviceType,
              rating: p.rating || 4.5,
              reviews: p.reviews || 0,
            };
          });

          setSwapZoneProducts(mapped);
        } else {
          setSwapZoneProducts([]);
        }
      } catch (err) {
        setSwapZoneProducts([]);
      } finally {
        setSwapZoneLoading(false);
      }
    };
    fetchSwapProducts();
  }, []);


  // Tick flashNow every second when there are flash deals (for per-deal countdowns)
  useEffect(() => {
    if (!flashDeals.length) return;
    const id = setInterval(() => setFlashNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [flashDeals]);

  // GSAP: Mousemove parallax for hero orbs
  useEffect(() => {
    const orb1 = orb1Ref.current;
    const orb2 = orb2Ref.current;
    const orb3 = orb3Ref.current;
    if (!orb1 || !orb2 || !orb3) return;

    const xSet1 = gsap.quickSetter(orb1, 'x', 'px');
    const ySet1 = gsap.quickSetter(orb1, 'y', 'px');
    const xSet2 = gsap.quickSetter(orb2, 'x', 'px');
    const ySet2 = gsap.quickSetter(orb2, 'y', 'px');
    const xSet3 = gsap.quickSetter(orb3, 'x', 'px');
    const ySet3 = gsap.quickSetter(orb3, 'y', 'px');

    const pos = { x: 0, y: 0 };

    const handleMouseMove = (e) => {
      const cx = e.clientX - window.innerWidth / 2;
      const cy = e.clientY - window.innerHeight / 2;
      pos.x = cx;
      pos.y = cy;
    };

    // Smooth update loop via GSAP ticker
    const update = () => {
      xSet1(pos.x * 0.02);
      ySet1(pos.y * 0.02);
      xSet2(pos.x * 0.04);
      ySet2(pos.y * 0.04);
      xSet3(pos.x * 0.015);
      ySet3(pos.y * 0.015);
    };

    window.addEventListener('mousemove', handleMouseMove);
    gsap.ticker.add(update);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      gsap.ticker.remove(update);
    };
  }, []);

  // GSAP: ScrollTrigger staggered unblur for categories
  useEffect(() => {
    if (loading) return;

    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray('.AutoboyExpress-category-card');
      if (cards.length) {
        gsap.fromTo(cards,
          { autoAlpha: 0, y: 40, filter: 'blur(16px)' },
          {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 0.7,
            stagger: 0.08,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: '.AutoboyExpress-categories-section',
              start: 'top 80%',
              once: true
            }
          }
        );
      }

      // Credibility items stagger
      const credItems = gsap.utils.toArray('.credibility-item');
      if (credItems.length) {
        gsap.fromTo(credItems,
          { autoAlpha: 0, y: 40, filter: 'blur(12px)' },
          {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 0.7,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: '.credibility-section',
              start: 'top 80%',
              once: true
            }
          }
        );
      }
    });

    return () => ctx.revert();
  }, [loading]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/shop?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleProductClick = (product) => {
    // Support both _id (MongoDB) and id formats
    const productId = product._id || product.id;
    window.location.href = `/product/${productId}`;
  };

  const handleSwap = (product, e) => {
    e.stopPropagation();
    if (product && (product.stock > 0 || product.quantity > 0)) {
      // Navigate to SwapDeals page with product context
      window.location.href = '/swap-deals';
      // Store product in sessionStorage for swap page to pick up
      sessionStorage.setItem('swapProduct', JSON.stringify({
        selectedProduct: product,
        action: 'create_offer'
      }));
      showCustomToast({
        type: 'success',
        title: 'Swap Initiated',
        message: `Prepare your swap offer for ${product.name || product.title}`,
        duration: 3000
      });
    } else {
      showCustomToast({
        type: 'warning',
        title: 'Out of Stock',
        message: 'This product is currently unavailable for swap',
        duration: 3000
      });
    }
  };

  const handleWishlistToggle = async (product, e) => {
    e.stopPropagation();

    if (!user) {
      showCustomToast({
        type: 'warning',
        title: 'Login Required',
        message: 'Please login to add items to your wishlist',
        duration: 3000
      });
      setTimeout(() => {
        window.location.href = '/auth';
      }, 1500);
      return;
    }

    const productId = product._id || product.id;
    const productName = product.name || product.title;

    try {
      const isInWishlist = wishlistItems.includes(productId);

      if (isInWishlist) {
        // Remove from wishlist
        await wishlistAPI.removeFromWishlist(productId);
        setWishlistItems(prev => prev.filter(id => id !== productId));
        showCustomToast({
          type: 'info',
          title: 'Removed from Wishlist',
          message: `${productName} removed from wishlist`
        });
      } else {
        // Add to wishlist
        await wishlistAPI.addToWishlist(productId);
        setWishlistItems(prev => [...prev, productId]);
        showCustomToast({
          type: 'success',
          title: 'Added to Wishlist',
          message: `${productName} added to wishlist`
        });
      }
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to update wishlist'
      });
    }
  };

  const handleAddToCart = async (product, e) => {
    e.stopPropagation(); // Prevent card click navigation

    if (!isAuthenticated) {
      showCustomToast({ type: 'warning', title: 'Sign Up Required', message: 'Create an account to add items to your cart.' });
      window.location.href = '/auth';
      return;
    }

    // Extract product ID - support both _id (MongoDB) and id formats
    const productId = product._id || product.id;


    // Validate product ID
    if (!productId) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: 'Invalid product ID. Please try again.'
      });
      return;
    }

    // Reseller category restriction
    if (isReseller) {
      // Backend product condition/category could use either nigeria_used or nigerian_used
      const condition = (product.condition || '').toLowerCase();
      const restrictedConditions = ['nigerian_used', 'nigeria_used', 'for_sale', 'want_to_buy'];
      if (restrictedConditions.includes(condition)) {
        showCustomToast({
          type: 'warning',
          title: 'Purchase Restriction',
          message: 'Resellers are not permitted to purchase from this category.'
        });
        return;
      }
    }

    try {
      await addToCart(productId, 1);
      showCustomToast({
        type: 'cart',
        title: 'Added to Cart!',
        message: `${product.name || product.title} has been added to your cart.`
      });
    } catch (error) {

      // Show detailed error message
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      showCustomToast({
        type: 'error',
        title: 'Failed to Add',
        message: `Could not add to cart: ${errorMsg}`
      });
    }
  };

  const handleCategoryClick = (category) => {
    // Use category name or slug for filtering
    const categoryParam = category.slug || category.name || category.id;
    window.location.href = `/shop?category=${encodeURIComponent(categoryParam)}`;
  };

  // Get icon for category based on name
  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();

    // Map category names to FontAwesome icons
    if (name.includes('phone') || name.includes('mobile') || name.includes('smartphone')) {
      return 'fa-mobile-alt';
    } else if (name.includes('airpod') || name.includes('earbuds') || name.includes('earpod')) {
      return 'fa-headphones';
    } else if (name.includes('laptop') || name.includes('notebook')) {
      return 'fa-laptop';
    } else if (name.includes('watch') || name.includes('smartwatch')) {
      return 'fa-clock';
    } else if (name.includes('console') || name.includes('gaming') || name.includes('playstation') || name.includes('xbox')) {
      return 'fa-gamepad';
    } else if (name.includes('headphone') || name.includes('audio') || name.includes('speaker')) {
      return 'fa-headphones-alt';
    } else if (name.includes('tablet') || name.includes('ipad')) {
      return 'fa-tablet-alt';
    } else if (name.includes('camera') || name.includes('photo')) {
      return 'fa-camera';
    } else if (name.includes('tv') || name.includes('television') || name.includes('monitor')) {
      return 'fa-tv';
    } else if (name.includes('computer') || name.includes('desktop') || name.includes('pc')) {
      return 'fa-desktop';
    } else if (name.includes('accessory') || name.includes('accessories')) {
      return 'fa-plug';
    } else if (name.includes('charger') || name.includes('cable')) {
      return 'fa-charging-station';
    } else {
      // Default icon for unknown categories
      return 'fa-shopping-bag';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(price.replace(/[\u20A6,]/g, ''));
  };

  const handleNewsletterSignup = (e) => {
    e.preventDefault();
    if (email) {
      showCustomToast({
        type: 'success',
        title: 'Welcome to AutoboyExpress!',
        message: 'Check your email for a 10% discount code.',
        duration: 6000
      });
      setEmail('');
    }
  };

  // ── Shared Swiper config ───────────────────────────────────────
  const swiperConfig = {
    modules: [Navigation, Pagination, Autoplay],
    spaceBetween: 20,
    slidesPerView: 1,
    navigation: true,
    pagination: { clickable: true },
    autoplay: { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true },
    breakpoints: {
      640: { slidesPerView: 2 },
      768: { slidesPerView: 3 },
      1024: { slidesPerView: 4 },
    },
    className: 'AutoboyExpress-products-swiper',
  };

  // ── Shared product slide renderer ─────────────────────────────
  const renderProductSlide = (product) => {
    const productStock = product.stock || product.quantity || 0;
    const productPrice = product.price || 0;
    const productName = product.name || product.title || 'Product';
    const pid = product._id || product.id;
    return (
      <SwiperSlide key={pid}>
        <TiltCard className={`hp-tilt-card AutoboyExpress-product-card-modern hp-deal-card ${productStock === 0 ? 'out-of-stock' : ''}`}>
          <div className="AutoboyExpress-quick-actions-modern">
            <button
              className={`AutoboyExpress-quick-action-modern ${wishlistItems.includes(pid) ? 'active' : ''}`}
              onClick={(e) => handleWishlistToggle(product, e)}
              title={wishlistItems.includes(pid) ? 'Remove from Wishlist' : 'Add to Wishlist'}
            >
              <i className={`${wishlistItems.includes(pid) ? 'fas' : 'far'} fa-heart`}></i>
            </button>
          </div>
          <div
            className="AutoboyExpress-product-image-modern"
            onClick={(e) => product._onSwap ? product._onSwap(product, e) : handleProductClick(product)}
            style={product._onSwap ? { cursor: 'pointer' } : undefined}
          >
            <ProductImageWithHover product={product} />
            {(product.is_trending || product.is_hot || product.is_featured) && (
              <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', zIndex: 3, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {product.is_featured && (
                  <span style={{ padding: '0.18rem 0.5rem', borderRadius: '20px', fontSize: '0.66rem', fontWeight: '700', background: 'linear-gradient(135deg,#10B981,#059669)', color: 'white', boxShadow: '0 2px 6px rgba(16,185,129,0.4)', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                    <i className="fas fa-tag" style={{ fontSize: '0.6rem' }} /> Good Deal
                  </span>
                )}
                {product.is_trending && (
                  <span style={{ padding: '0.18rem 0.5rem', borderRadius: '20px', fontSize: '0.66rem', fontWeight: '700', background: 'linear-gradient(135deg,#F59E0B,#D97706)', color: 'white', boxShadow: '0 2px 6px rgba(245,158,11,0.4)', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                    <i className="fas fa-chart-line" style={{ fontSize: '0.6rem' }} /> Trending
                  </span>
                )}
                {product.is_hot && (
                  <span style={{ padding: '0.18rem 0.5rem', borderRadius: '20px', fontSize: '0.66rem', fontWeight: '700', background: 'linear-gradient(135deg,#EF4444,#DC2626)', color: 'white', boxShadow: '0 2px 6px rgba(239,68,68,0.4)', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                    <i className="fas fa-fire" style={{ fontSize: '0.6rem' }} /> Hot
                  </span>
                )}
              </div>
            )}
            {product.isNew && <span className="AutoboyExpress-badge-modern new">New</span>}
            {productStock === 0 && <span className="AutoboyExpress-badge-modern out-stock">Out of Stock</span>}
            {product.discount && <span className="AutoboyExpress-badge-modern discount">-{product.discount}</span>}
            {product._badge}
          </div>
          <div className="AutoboyExpress-product-info-modern">
            <h3
              className="AutoboyExpress-product-name-modern"
              onClick={(e) => product._onSwap ? product._onSwap(product, e) : handleProductClick(product)}
            >
              {productName}
            </h3>
            <div className="AutoboyExpress-price-stock-row">
              <div className="AutoboyExpress-product-price-modern">
                {isSeller && productPrice && (product.seller_price || product.sellerPrice) ? (
                  <div className="hp-price-pill hp-price-dual">
                    <div className="hp-price-row">
                      <span className="hp-price-label">Retail:</span>
                      <span className="hp-price-value">{'\u20A6'}{productPrice.toLocaleString('en-NG')}</span>
                    </div>
                    <div className="hp-price-row hp-price-wholesale">
                      <span className="hp-price-label hp-wholesale-label">Wholesale:</span>
                      <span className="hp-price-value hp-wholesale-value">{'\u20A6'}{(product.seller_price || product.sellerPrice).toLocaleString('en-NG')}</span>
                    </div>
                  </div>
                ) : (
                  <span className="hp-price-pill">{'\u20A6'}{productPrice.toLocaleString('en-NG')}</span>
                )}
              </div>
              {productStock > 0 && <span className="AutoboyExpress-stock-badge">{productStock} left</span>}
            </div>
            {/* Seller info with identity masking */}
            <div className="AutoboyExpress-seller-info-modern">
              <span className="AutoboyExpress-seller-name-label">
                {product.seller_premium
                  ? (product.seller_name || product.seller?.name || 'Seller')
                  : `Seller ${String(product.seller_id || '000').slice(-3).padStart(3, '0')}`
                }
              </span>
              {product.seller_premium && (
                <span className="AutoboyExpress-product-premium-badge">
                  <i className="fas fa-crown"></i> Premium
                </span>
              )}
              {product.seller_verified && (
                <span className="AutoboyExpress-product-verified-badge">
                  <i className="fas fa-check"></i> Verified
                </span>
              )}
            </div>
            <div className="AutoboyExpress-action-buttons-modern">
              <AnimatedViewButton
                onClick={(e) => { e.stopPropagation(); handleProductClick(product); }}
                className="AutoboyExpress-view-btn-modern"
              >
                View
              </AnimatedViewButton>
              {!product._onSwap && (
                <>
                  <button
                    onClick={(e) => handleSwap(product, e)}
                    disabled={productStock === 0}
                    className="AutoboyExpress-swap-btn-modern"
                    title="Swap this product"
                  >
                    <i className="fas fa-exchange-alt"></i>
                  </button>
                  <AnimatedAddToCartButton
                    onClick={(e) => handleAddToCart(product, e)}
                    disabled={productStock === 0}
                    className="AutoboyExpress-cart-btn-modern"
                  />
                </>
              )}
            </div>
          </div>
        </TiltCard>
      </SwiperSlide>
    );
  };

  // ── Flash Deal products (Admin -> Products -> Flash Deal) — real products,
  // real prices, real IDs. Clicking one always resolves to a real product
  // page, unlike the old version of this section which mapped sitewide promo
  // campaigns (no product attached) into fake product cards. ──
  const flashDealProducts = flashDealProductsData.map(p => {
    const price = p.flash_deal_price || p.price || 0;
    const origPrice = p.price;
    const discountPct = origPrice && price && origPrice > price ? Math.round((1 - price / origPrice) * 100) : null;
    const expires = p.flash_deal_ends_at;
    const diff = expires ? new Date(expires) - flashNow : null;
    const timerStr = diff && diff > 0 ? (() => {
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    })() : null;
    return {
      ...p,
      price,
      image: proxyImageUrl(p.image),
      discount: discountPct ? `${discountPct}%` : null,
      _badge: (
        <span className="AutoboyExpress-badge-modern hp-flash-timer">
          <i className={`fas ${p.is_early_access ? 'fa-hourglass-half' : 'fa-fire'}`} />
          {' '}{p.is_early_access ? 'EARLY ACCESS' : (timerStr || 'DEAL')}
        </span>
      ),
    };
  });

  // ── Active flash sale for the hero banner (real admin data, not static copy) ──
  const activeFlashDeal = (() => {
    const now = flashNow;
    const active = flashDeals.filter(d => {
      const endsAt = d.end_date || d.expires_at || d.end_time || d.ends_at;
      const isActive = d.is_active !== false;
      return isActive && (!endsAt || new Date(endsAt).getTime() > now);
    });
    if (active.length === 0) return null;
    // Soonest-expiring deal first, so the banner reflects the most urgent sale
    return active.sort((a, b) => {
      const aEnd = new Date(a.end_date || a.expires_at || a.end_time || a.ends_at || 0).getTime();
      const bEnd = new Date(b.end_date || b.expires_at || b.end_time || b.ends_at || 0).getTime();
      return aEnd - bEnd;
    })[0];
  })();

  const heroFlashCountdown = (() => {
    if (!activeFlashDeal) return null;
    const endsAt = activeFlashDeal.end_date || activeFlashDeal.expires_at || activeFlashDeal.end_time || activeFlashDeal.ends_at;
    if (!endsAt) return null;
    const diff = new Date(endsAt).getTime() - flashNow;
    if (diff <= 0) return null;
    return {
      hours: Math.floor(diff / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  })();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'var(--hp-bg, #F0F4F8)'
      }}>
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="homepage">
        {/* SVG Filter for Liquid Crystal Effect - Must be included once */}
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <filter id="liquid-glass-distortion" x="0%" y="0%" width="100%" height="100%">
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.012 0.012"
                numOctaves="2"
                seed="92"
                result="noise"
              />
              <feGaussianBlur
                in="noise"
                stdDeviation="2"
                result="blurred"
              />
              <feDisplacementMap
                in="SourceGraphic"
                in2="blurred"
                scale="85"
                xChannelSelector="R"
                yChannelSelector="G"
              />
            </filter>
          </defs>
        </svg>

        {/* ============================================
          HERO SECTION - Liquid Glass Immersion
          ============================================ */}
        <section className="hp-hero AutoboyExpress-hero-section AutoboyExpress-liquid-glass-hero">
          {/* Animated Background Image with Zoom Effect */}
          <div className="AutoboyExpress-hero-bg-image">
            <div className="AutoboyExpress-zoom-image"></div>
          </div>

          {/* Liquid Glass Blur Layer */}
          <div className="AutoboyExpress-liquid-glass-overlay"></div>

          {/* Glass Tint and Inner Shadow */}
          <div className="AutoboyExpress-glass-tint"></div>

          {/* Floating Orbs (react to mouse via GSAP quickSetter) */}
          <div className="AutoboyExpress-hero-bg-animation">
            <div ref={orb1Ref} className="AutoboyExpress-floating-orb AutoboyExpress-orb-1"></div>
            <div ref={orb2Ref} className="AutoboyExpress-floating-orb AutoboyExpress-orb-2"></div>
            <div ref={orb3Ref} className="AutoboyExpress-floating-orb AutoboyExpress-orb-3"></div>
          </div>

          {/* Floating Particles */}

          <div className="AutoboyExpress-hero-container">
            <motion.div
              className="AutoboyExpress-hero-content"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <div className="AutoboyExpress-hero-inner">
                <div className="AutoboyExpress-hero-text">
                  <motion.h2
                    className="AutoboyExpress-hero-subtitle"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                  >
                    Welcome to AutoboyExpress
                  </motion.h2>
                  <motion.h1
                    className="AutoboyExpress-hero-title"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.7 }}
                  >
                    Buy, Sell,
                    <span className="AutoboyExpress-gradient-text"> Swap Gadgets.</span>
                  </motion.h1>
                  <motion.p
                    className="AutoboyExpress-hero-description"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.9 }}
                  >
                    Your ultimate destination for premium gadgets. Buy the latest smartphones, sell your old devices,
                    or swap for something new. Experience unbeatable deals on electronics you love.
                  </motion.p>
                  <motion.div
                    className="AutoboyExpress-hero-buttons"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.1 }}
                  >
                    <motion.a
                      href="/shop"
                      className="AutoboyExpress-btn AutoboyExpress-btn-primary"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <i className="fas fa-shopping-bag"></i>
                      Shop Now
                    </motion.a>
                    <motion.button
                      className="AutoboyExpress-btn AutoboyExpress-btn-secondary hp-btn-glass"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        document.querySelector('.AutoboyExpress-deals-section')?.scrollIntoView({
                          behavior: 'smooth'
                        });
                      }}
                    >
                      <i className="fas fa-bolt"></i>
                      Browse Gadgets
                    </motion.button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ============================================
          FLASH SALE BANNER — reflects the admin's active flash sale
          (Admin → Marketing → Flash Sales). Hidden entirely when none
          are active, rather than showing static placeholder copy.
          ============================================ */}
        {activeFlashDeal && (
          <section className="flash-sale-banner hp-flash-sale">
            <div className="flash-sale-container">
              <motion.div
                className="flash-sale-content"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flash-sale-text">
                  <div className="hp-live-badge">
                    <span className="hp-live-dot"></span>
                    <span className="hp-live-text">LIVE</span>
                  </div>
                  <h3 className="flash-sale-title">
                    <i className="fas fa-bolt flash-sale-icon"></i>
                    {activeFlashDeal.title || 'Flash Sale - Limited Time!'}
                  </h3>
                  <p className="flash-sale-subtitle">
                    {activeFlashDeal.description ||
                      (activeFlashDeal.discount_percent ? `Up to ${activeFlashDeal.discount_percent}% off on selected gadgets` : 'Limited time offer')}
                  </p>
                </div>
                {heroFlashCountdown && (
                  <div className="flash-sale-timer hp-cyber-timer">
                    <div className="timer-label">Deal expires in:</div>
                    <div className="countdown-timer">
                      <div className="time-unit">
                        <span className="time-value">{String(heroFlashCountdown.hours).padStart(2, '0')}</span>
                        <span className="time-label">Hours</span>
                      </div>
                      <div className="time-separator">:</div>
                      <div className="time-unit">
                        <span className="time-value">{String(heroFlashCountdown.minutes).padStart(2, '0')}</span>
                        <span className="time-label">Minutes</span>
                      </div>
                      <div className="time-separator">:</div>
                      <div className="time-unit">
                        <span className="time-value">{String(heroFlashCountdown.seconds).padStart(2, '0')}</span>
                        <span className="time-label">Seconds</span>
                      </div>
                    </div>
                  </div>
                )}
                <motion.a
                  href="/shop"
                  className="flash-sale-cta"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Shop Flash Deals
                  <i className="fas fa-bolt"></i>
                </motion.a>
              </motion.div>
            </div>
          </section>
        )}

        {/* ============================================
          PRODUCTS SECTION
          ============================================ */}
        <section className="AutoboyExpress-products-section">
          <div className="AutoboyExpress-products-container">

            {/* Flash Deals */}
            {flashDealProducts.length > 0 && (
              <div className="AutoboyExpress-deals-section hp-flash-deals-section">
                <motion.div className="AutoboyExpress-section-header" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                  <h2 className="AutoboyExpress-section-title force-visible"><i className="fas fa-fire hp-fire-icon"></i> Flash Deals</h2>
                  <motion.a href="/shop" className="AutoboyExpress-more-btn" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}><span>View All</span><i className="fas fa-arrow-right"></i></motion.a>
                </motion.div>
                <motion.div className="AutoboyExpress-deals-slider-container" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }}>
                  <Swiper {...swiperConfig}>{flashDealProducts.map(renderProductSlide)}</Swiper>
                </motion.div>
              </div>
            )}

            {/* Categories */}
            <div className="AutoboyExpress-categories-section">
              <motion.div
                className="AutoboyExpress-section-header force-visible"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="AutoboyExpress-section-title force-visible">Devices</h2>
                <motion.a
                  href="/shop"
                  className="AutoboyExpress-more-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>More</span>
                  <i className="fas fa-arrow-right"></i>
                </motion.a>
              </motion.div>

              <div
                className="AutoboyExpress-categories-grid"
                id="autoboyCategories"
              >
                {categories.map((category, index) => (
                  <div
                    key={category.id}
                    className="AutoboyExpress-category-card"
                    onClick={() => handleCategoryClick(category)}
                  >
                    <div className="AutoboyExpress-category-icon">
                      <i className={`fas ${getCategoryIcon(category.name)}`}></i>
                    </div>
                    <h3 className="AutoboyExpress-category-name">{category.name}</h3>
                  </div>
                ))}
              </div>
            </div>

            {/* Deals of the Week */}
            <div className="AutoboyExpress-deals-section">
              <motion.div
                className="AutoboyExpress-section-header"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="AutoboyExpress-section-title force-visible">Good Deals</h2>
                <motion.a
                  href="/shop"
                  className="AutoboyExpress-more-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>More</span>
                  <i className="fas fa-arrow-right"></i>
                </motion.a>
              </motion.div>

              {/* Deals Slider - Using Swiper for smooth carousel */}
              <motion.div
                className="AutoboyExpress-deals-slider-container"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
                id="autoboyDeals"
              >
                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  spaceBetween={20}
                  slidesPerView={1}
                  navigation
                  pagination={{ clickable: true }}
                  autoplay={{
                    delay: 4000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true
                  }}
                  breakpoints={{
                    640: {
                      slidesPerView: 2,
                      spaceBetween: 20
                    },
                    768: {
                      slidesPerView: 3,
                      spaceBetween: 20
                    },
                    1024: {
                      slidesPerView: 4,
                      spaceBetween: 24
                    },
                    1280: {
                      slidesPerView: 4,
                      spaceBetween: 24
                    }
                  }}
                  className="AutoboyExpress-products-swiper"
                >
                  {(products.filter(p => p.is_featured).length > 0
                    ? products.filter(p => p.is_featured)
                    : products
                  ).map(renderProductSlide)}
                </Swiper>
              </motion.div>
            </div>


            {/* Trending Now */}
            {trendingProducts.length > 0 && (
              <div className="AutoboyExpress-deals-section">
                <motion.div className="AutoboyExpress-section-header" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                  <h2 className="AutoboyExpress-section-title force-visible"><i className="fas fa-chart-line hp-trend-icon"></i> Trending Now</h2>
                  <motion.a href="/shop" className="AutoboyExpress-more-btn" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}><span>More</span><i className="fas fa-arrow-right"></i></motion.a>
                </motion.div>
                <motion.div className="AutoboyExpress-deals-slider-container" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }}>
                  <Swiper {...swiperConfig}>
                    {trendingProducts.map(p => renderProductSlide({ ...p, _badge: <span className="AutoboyExpress-badge-modern hp-trending-badge"><i className="fas fa-chart-line"></i> Hot</span> }))}
                  </Swiper>
                </motion.div>
              </div>
            )}

            {/* Just For You */}
            {user && recommendations.length > 0 && (
              <div className="AutoboyExpress-deals-section">
                <motion.div className="AutoboyExpress-section-header" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                  <h2 className="AutoboyExpress-section-title force-visible"> Just For You</h2>
                  <motion.a href="/shop" className="AutoboyExpress-more-btn" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}><span>More</span><i className="fas fa-arrow-right"></i></motion.a>
                </motion.div>
                <motion.div className="AutoboyExpress-deals-slider-container" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }}>
                  <Swiper {...swiperConfig}>
                    {recommendations.map(p => renderProductSlide({ ...p, _badge: <span className="AutoboyExpress-badge-modern hp-rec-badge"><i className="fas fa-star"></i> For You</span> }))}
                  </Swiper>
                </motion.div>
              </div>
            )}

            {/* ============================================
              SWAP ZONE - Trade Your Device
              ============================================ */}
            {(swapZoneLoading || swapZoneProducts.length > 0) && (
              <div className="AutoboyExpress-deals-section hp-swap-zone-section">
                {/* ── Swap Zone Header ── */}
                <motion.div
                  className="AutoboyExpress-section-header hp-swap-zone-header"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="hp-swap-zone-title-group">
                    <h2 className="AutoboyExpress-section-title force-visible hp-swap-zone-title">

                      Swap Zone
                    </h2>
                    <span className="hp-swap-zone-pill">
                      <span className="hp-swap-zone-pulse-dot"></span>
                      Trade &amp; Upgrade
                    </span>
                  </div>
                  <motion.a
                    href="/swap-deals"
                    className="AutoboyExpress-more-btn"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>View All</span>
                    <i className="fas fa-arrow-right"></i>
                  </motion.a>
                </motion.div>

                {/* ── Swap Zone Slider ── */}
                <motion.div
                  className="AutoboyExpress-deals-slider-container"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  {swapZoneLoading ? (
                    <div className="hp-skeleton-row">
                      {[...Array(4)].map((_, i) => <div key={i} className="hp-skeleton-card" />)}
                    </div>
                  ) : (
                    <Swiper {...swiperConfig}>
                      {swapZoneProducts.map(p => renderProductSlide({
                        ...p,
                        // Override price display to show min swap value
                        price: p.min_swap_value || p.price || 0,
                        // "Swap Ready" badge at bottom-left of image
                        _badge: (
                          <span className="AutoboyExpress-badge-modern hp-swap-zone-badge">
                            <i className="fas fa-exchange-alt"></i> Swap Ready
                          </span>
                        ),
                        // Override swap button to open modal instead of navigating
                        _onSwap: (prod, e) => {
                          e.stopPropagation();
                          setSelectedProductForSwap(prod);
                          setShowSwapModal(true);
                        },
                      }))}
                    </Swiper>
                  )}
                </motion.div>
              </div>
            )}

            {/* ============================================
              DYNAMIC PROMO STACKS
              ============================================ */}
            {promos.length > 0 && (
              <div className="AutoboyExpress-deals-section hp-promo-stacks-section">
                <motion.div className="AutoboyExpress-section-header" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                  <h2 className="AutoboyExpress-section-title force-visible">
                    <i className="fas fa-tags hp-promo-icon" /> Hot Promotions
                  </h2>
                  <div className="hp-promo-nav-row">
                    <button className="hp-promo-nav-btn" onClick={() => setPromoIndex(i => (i - 1 + promos.length) % promos.length)}>
                      <i className="fas fa-chevron-left" />
                    </button>
                    <span className="hp-promo-counter">{promoIndex + 1} / {promos.length}</span>
                    <button className="hp-promo-nav-btn" onClick={() => setPromoIndex(i => (i + 1) % promos.length)}>
                      <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                </motion.div>
                <motion.div className="hp-promo-stack-wrapper" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }}>
                  {promos.map((promo, i) => (
                    <PromoCard
                      key={promo.id || i}
                      promo={promo}
                      index={i}
                      activeIndex={promoIndex}
                      total={promos.length}
                      onSwipeDone={(dir) => setPromoIndex(prev => (prev + (dir === 'left' ? 1 : -1) + promos.length) % promos.length)}
                    />
                  ))}
                </motion.div>
                <div className="hp-promo-dots">
                  {promos.map((_, i) => (
                    <button key={i} className={`hp-promo-dot${i === promoIndex ? ' hp-promo-dot--active' : ''}`} onClick={() => setPromoIndex(i)} />
                  ))}
                </div>
              </div>
            )}

            {/* View All Products Button */}
            <motion.div
              className="AutoboyExpress-view-all-container"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <motion.a
                href="/shop"
                className="AutoboyExpress-view-all-btn"
                style={{ '--mx': 0, '--my': 0 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty('--mx', (e.clientX - r.left - r.width / 2) / (r.width / 2));
                  e.currentTarget.style.setProperty('--my', (e.clientY - r.top - r.height / 2) / (r.height / 2));
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.setProperty('--mx', 0);
                  e.currentTarget.style.setProperty('--my', 0);
                }}
              >
                <span>View All Products</span>
                <i className="fas fa-arrow-right"></i>
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* ============================================
          NEWSLETTER - Bento Box
          ============================================ */}
        <section className="newsletter-section">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="newsletter-content hp-newsletter-bento"
            >
              <div className="hp-bento-left">
                <h2 className="newsletter-title">
                  <i className="fas fa-envelope newsletter-icon"></i>
                  Get Exclusive Deals & Updates
                </h2>
                <p className="newsletter-subtitle">
                  Subscribe to our newsletter and get 10% off your first order plus early access to flash sales!
                </p>
                <div className="newsletter-benefits">
                  <span className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    Exclusive discount codes
                  </span>
                  <span className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    Early access to sales
                  </span>
                  <span className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    Latest tech news
                  </span>
                </div>
              </div>

              <div className="hp-bento-right">
                <form onSubmit={handleNewsletterSignup} className="newsletter-form">
                  <div className="newsletter-input-group">
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="newsletter-input"
                      required
                    />
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.06, boxShadow: '0 0 30px rgba(34,197,94,0.5)' }}
                      whileTap={{ scale: 0.95 }}
                      className="newsletter-btn hp-mag-btn"
                    >
                      <span>Get 10% Off</span>
                      <i className="fas fa-paper-plane"></i>
                    </motion.button>
                  </div>
                  <p className="newsletter-privacy">
                    <i className="fas fa-lock"></i>
                    We respect your privacy. Unsubscribe at any time.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ============================================
          CREDIBILITY SECTION
          ============================================ */}
        <section className="credibility-section">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="credibility-content"
            >
              <h2 className="credibility-title">Why Choose AutoboyExpress?</h2>
              <div className="credibility-grid">
                <div className="credibility-item">
                  <div className="credibility-icon">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <h3>100% Authentic</h3>
                  <p>All products are genuine and come with manufacturer warranty</p>
                </div>
                <div className="credibility-item">
                  <div className="credibility-icon">
                    <i className="fas fa-truck-fast"></i>
                  </div>
                  <h3>Fast Delivery</h3>
                  <p>Same-day delivery in Lagos, nationwide shipping available</p>
                </div>
                <div className="credibility-item">
                  <div className="credibility-icon">
                    <i className="fas fa-headset"></i>
                  </div>
                  <h3>24/7 Support</h3>
                  <p>Expert customer support team ready to help anytime</p>
                </div>
                <div className="credibility-item">
                  <div className="credibility-icon">
                    <i className="fas fa-medal"></i>
                  </div>
                  <h3>5+ Years Experience</h3>
                  <p>Trusted by thousands of customers since 2019</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ============================================
          PAYMENT & SHIPPING ASSURANCE
          ============================================ */}
        <section className="assurance-section">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="assurance-content"
            >
              <div className="assurance-grid">
                <div className="assurance-column">
                  <h3 className="assurance-title">
                    <i className="fas fa-credit-card"></i>
                    Secure Payment
                  </h3>
                  <div className="payment-methods">
                    <img src="https://img.icons8.com/color/48/visa.png" alt="Visa" />
                    <img src="https://img.icons8.com/color/48/mastercard.png" alt="Mastercard" />
                    <img src="https://img.icons8.com/color/48/paypal.png" alt="PayPal" />
                    <img src="https://img.icons8.com/color/48/bank-cards.png" alt="Bank Transfer" />
                  </div>
                  <p>Your payment information is encrypted and secure</p>
                </div>

                <div className="assurance-column">
                  <h3 className="assurance-title">
                    <i className="fas fa-shipping-fast"></i>
                    Shipping Options
                  </h3>
                  <div className="shipping-options">
                    <div className="shipping-option">
                      <i className="fas fa-bolt"></i>
                      <span>Same-day delivery (Lagos)</span>
                    </div>
                    <div className="shipping-option">
                      <i className="fas fa-truck"></i>
                      <span>2-3 days nationwide</span>
                    </div>
                    <div className="shipping-option">
                      <i className="fas fa-store"></i>
                      <span>Pickup available</span>
                    </div>
                  </div>
                </div>

                <div className="assurance-column">
                  <h3 className="assurance-title">
                    <i className="fas fa-undo-alt"></i>
                    Easy Returns
                  </h3>
                  <div className="return-policy">
                    <div className="return-item">
                      <i className="fas fa-calendar-alt"></i>
                      <span>30-day return policy</span>
                    </div>
                    <div className="return-item">
                      <i className="fas fa-exchange-alt"></i>
                      <span>Free exchanges</span>
                    </div>
                    <div className="return-item">
                      <i className="fas fa-money-bill-wave"></i>
                      <span>Full refund guarantee</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

      </div>

      {/* ============================================================
        SWAP ZONE MODAL
        Opens when the ⇄ button is clicked on a Swap Zone card.
        Shows product preview + SwapOfferForm (device-type aware).
        ============================================================ */}
      <AnimatePresence>
        {showSwapModal && selectedProductForSwap && (
          <motion.div
            className="hp-szm-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setShowSwapModal(false)}
          >
            <motion.div
              className={`hp-szm-sheet ${selectedProductForSwap.device_type?.toLowerCase() === 'ios'
                  ? 'ios-style'
                  : selectedProductForSwap.device_type?.toLowerCase() === 'android'
                    ? 'android-style'
                    : ''
                }`}
              initial={{ y: 60, opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 60, opacity: 0, scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 320, damping: 26, mass: 0.8 }}
              onClick={e => e.stopPropagation()}
            >
              {/* Sticky header */}
              <motion.div
                className="hp-szm-header"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: 0.1, duration: 0.3, type: 'spring', stiffness: 350, damping: 25 }}
                style={{ willChange: 'transform, opacity' }}
              >
                <div className="hp-szm-header-left">
                  <span className="hp-szm-header-icon">
                    <i className="fas fa-exchange-alt"></i>
                  </span>
                  <span className="hp-szm-header-title">Trade Your Device</span>
                </div>
                <motion.button
                  className="hp-szm-close"
                  onClick={() => setShowSwapModal(false)}
                  whileHover={{ rotate: 90, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  <i className="fas fa-times"></i>
                </motion.button>
              </motion.div>

              {/* Product preview strip */}
              <motion.div
                className="hp-szm-product-strip"
                initial={{ x: -16, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.35, type: 'spring', stiffness: 380, damping: 28 }}
                style={{ willChange: 'transform, opacity' }}
              >
                <motion.img
                  src={
                    selectedProductForSwap.images?.[0]?.url ||
                    (typeof selectedProductForSwap.images?.[0] === 'string' ? selectedProductForSwap.images[0] : null) ||
                    selectedProductForSwap.image
                  }
                  alt={selectedProductForSwap.name}
                  className="hp-szm-product-img"
                  loading="lazy"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.4, type: 'spring', stiffness: 360, damping: 22 }}
                  style={{ willChange: 'transform, opacity' }}
                />
                <div className="hp-szm-product-info">
                  <p className="hp-szm-product-name">{selectedProductForSwap.name || selectedProductForSwap.title}</p>
                  {selectedProductForSwap.device_type && (
                    <span className="hp-szm-device-tag">
                      <i className={`fas fa-${selectedProductForSwap.device_type?.toLowerCase() === 'laptop' ? 'laptop' : 'mobile-alt'}`}></i>
                      {selectedProductForSwap.device_type}
                    </span>
                  )}
                  <p className="hp-szm-min-value">
                    Min. Swap Value&nbsp;
                    <strong>₦{(selectedProductForSwap.min_swap_value || 0).toLocaleString()}</strong>
                  </p>
                </div>
              </motion.div>

              {/* Swap offer form */}
              <motion.div
                className="hp-szm-form-wrap"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.45, type: 'spring', stiffness: 340, damping: 30 }}
                style={{ willChange: 'transform, opacity' }}
              >
                <SwapOfferForm
                  productId={selectedProductForSwap.id || selectedProductForSwap._id}
                  onSuccess={() => {
                    setShowSwapModal(false);
                    showCustomToast({
                      type: 'success',
                      title: 'Swap Offer Submitted!',
                      message: `Your offer for ${selectedProductForSwap.name || 'this product'} has been sent to the seller.`,
                      duration: 5000,
                    });
                  }}
                  onCancel={() => setShowSwapModal(false)}
                  showSummary={true}
                />
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Homepage;
